<script setup>
import { ref, computed, onMounted, watchEffect, watch } from 'vue';
import { useSpotStore } from '@/stores/SpotStore';
import { useUserStore } from '@/stores/UserStore';
import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
import { useSortSpotsBy } from '@/composables/useSortSpotsBy';
import { useUpdateKey } from '@/composables/useUpdateKey';
import useStorageSystem from '@/composables/useStorageSystem';
import SpotList from '@/components/SpotList.vue';
import DataContainer from '@/components/DataContainer.vue';
import DataContainerSection from '@/components/DataContainerSection.vue';
import ModalUpgradeSpots from '@/components/ModalUpgradeSpots.vue';
import SpotSortOrderSelector from '@/components/SpotSortOrderSelector.vue';
import LiveWindMap from '@/components/LiveWindMap.vue';
import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
import ModalBrandPromo from '@/components/ModalBrandPromo.vue';
import DataContainerLoader from '@/components/DataContainerLoader.vue';
import useSettingsGlobals from '@/composables/useSettingsGlobals';

const { $SPOT_DISTANCE_GROUPING, $PROMO_MODAL_ALLOWED } = useSettingsGlobals();   

const emit = defineEmits(['loaded', 'error']);

const PATHNAME = window.location.pathname;

const spotStore = useSpotStore();
const userStore = useUserStore();
const googleAnalyticsStore = useGoogleAnalyticsStore();

const updateKey = useUpdateKey()

await userStore.initialize()

const [mapState] = useStorageSystem({}, 'lastLocation', 'localStorage');

const enableDistanceGrouping = $SPOT_DISTANCE_GROUPING;

const spotsData = ref([]);
const isLoading = ref(false);
const error = ref(false);
const errorMessage = ref('');
const spots = ref([]);
const upgradeSpots = ref([]);
const closeSpots = ref([]);
const farSpots = ref([]);
const closeUpgradeSpots = ref([]);
const farUpgradeSpots = ref([]);
const showUpgradeSpotModal = ref(false);
const displayLiveWindMap = ref(false);
const mapSearchTerm = ref('');
const mapSearchType = ref('search');
const mapLat = ref(null);
const mapLon = ref(null);
const mapZoom = ref(9);
const mapModelId = ref(-1);
const sortOrder = ref('default');

const closeSpotsDistance = computed(() => userStore.units.distance === 'mi' ? 20 : 32);
const farSpotsDistance = computed(() => userStore.units.distance === 'mi' ? 65 : 105);
const closeSpotsSectionTitle = computed(() => `Spots within ${closeSpotsDistance.value} ${userStore.units.distance === 'mi' ? 'Miles' : 'Kilometers'}`);
const farSpotsSectionTitle = computed(() => `Spots within ${farSpotsDistance.value} ${userStore.units.distance === 'mi' ? 'Miles' : 'Kilometers'}`);
const closeSpotsSorted = useSortSpotsBy(closeSpots, sortOrder);
const farSpotsSorted = useSortSpotsBy(farSpots, sortOrder);
const spotsSorted = useSortSpotsBy(spots, sortOrder);
const sortedUpgradeSpots = computed(() => [...upgradeSpots.value, ...closeUpgradeSpots.value, ...farUpgradeSpots.value].sort((spotA, spotB) => spotA.distance - spotB.distance))
const showUpgradeBlock = computed(() => upgradeSpots.value.length > 0 || closeUpgradeSpots.value.length > 0 || farUpgradeSpots.value.length > 0);
const totalUpgradeSpots = computed(() => upgradeSpots.value.length + closeUpgradeSpots.value.length + farUpgradeSpots.value.length);
const spotDistance = computed(() => userStore.units.distance === 'mi' ? 65 : 105);
const hasSpots = computed(() => enableDistanceGrouping ? closeSpotsSorted.value.length > 0 : spotsSorted.value.length > 0);
const showAds = computed(() => userStore.showAds || userStore.memberLevel < 46);
const showPromoModal = computed(() => userStore.messages && userStore.messages.length > 0 && parseInt(userStore.nonTrialMemberLevel) < 49 && $PROMO_MODAL_ALLOWED);

const totalSpotCount = computed(() => enableDistanceGrouping
  ? closeSpotsSorted.value.length + farSpotsSorted.value.length
  : spotsSorted.value.length
);
const totalAdCount = computed(() => {
  if (!showAds.value || totalSpotCount.value < 24) return 0;
  return Math.floor((totalSpotCount.value - 12) / 12);
});
const allAdIndexes = computed(() => {
  const indexes = [];
  for (let i = 12; i < totalSpotCount.value && indexes.length < totalAdCount.value; i += 12) {
    indexes.push(i);
  }
  return indexes;
});
const closeAdIndexes = computed(() => allAdIndexes.value.filter(i => i < closeSpotsSorted.value.length));
const farAdIndexes = computed(() => {
  const farLength = farSpotsSorted.value.length;
  const rebased = allAdIndexes.value
    .filter(i => i >= closeSpotsSorted.value.length)
    .map(i => i - closeSpotsSorted.value.length);

  if (rebased.length === 0) return [];

  const offset = rebased[0] < 3 ? 3 - rebased[0] : 0;

  return rebased
    .map(i => i + offset)
    .filter(i => i <= farLength - 12);
});
const spotsAdIndexes = computed(() => allAdIndexes.value);
const hasNoSpotsMessage = computed(() => {
  return !enableDistanceGrouping || enableDistanceGrouping && farSpotsSorted.value.length === 0 
    ? `There are no ${farSpotsSectionTitle.value.toLowerCase()} of this location`
    : `There are no ${closeSpotsSectionTitle.value.toLowerCase()} of this location`
})

const displayLiveWind = () => {
  const params = new URLSearchParams(window.location.search);

  // local dev env
  if (PATHNAME.indexOf('/windlist-lifestyle/') > -1) return params.get('map') === "true";

  if (PATHNAME.indexOf('/search') > -1) return true;
}

// Helper function to extract path segments from URL
const getPathSegments = () => {
  // Get pathname from browser API (e.g., "/windlist/Victoria-AU" or "/windlist/-36.9847807/143.3906074")
  let search = "";
  
  if (PATHNAME.indexOf('/windlist-lifestyle/') > -1) {
    const params = new URLSearchParams(window.location.search);

    search = params.get('search');
  }

  if (PATHNAME.indexOf('/windlist/') > -1) {
    search = PATHNAME.split('/windlist/')[1];
  }

  if (PATHNAME.indexOf('/search/') > -1) {
    search = PATHNAME.split('/search/')[1];
  }

  if (!search) return [];

  return [...search.split('/')]
};

const sortSpots = (spots) => {
  const viewableSpots = [];
  const upgradeSpots = [];

  spots.forEach(spot => {
    if (spot.upgrade_available) {
      upgradeSpots.push(spot)
    } else {
      viewableSpots.push(spot)
    }
  });

  return [viewableSpots, upgradeSpots];
}

const sortSpotsByDistance = (spots) => {
  const newCloseSpots = [];
  const newCloseUpgradeSpots = [];
  const newFarSpots = [];
  const newFarUpgradeSpots = [];

  spots.forEach(spot => {
    if (spot.distance <= closeSpotsDistance.value) {
      if (spot.upgrade_available) {
        newCloseUpgradeSpots.push(spot);
      } else {
        newCloseSpots.push(spot);
      }
    }
    else if (spot.distance > closeSpotsDistance.value && spot.distance <= farSpotsDistance.value) {
      if (spot.upgrade_available) {
        newFarUpgradeSpots.push(spot);
      } else {
        newFarSpots.push(spot);
      }
    }
  });

  return [newCloseSpots, newFarSpots, newCloseUpgradeSpots, newFarUpgradeSpots];
}

// Parse URL and fetch data
const fetchSpots = async (searchTerm) => {
  isLoading.value = true;
  error.value = false;
  errorMessage.value = '';

  const settings = {
    sort: "star",
    search_dist: spotDistance.value,
    page: 1,
    num_per_page: 50
  }

  try {
    const data = await spotStore.getSpotDetailSetBySearch(searchTerm, settings);
    
    if (data && data.error) {
      throw new Error(data.message || 'Unable to load weather stations. Please try again.');
    }

    if (data && data.spots && data.spots.length > 0) {
      spotsData.value = data.spots;

      if (enableDistanceGrouping) {
        [closeSpots.value, farSpots.value, closeUpgradeSpots.value, farUpgradeSpots.value] = sortSpotsByDistance(spotsData.value);
      } else {
        [spots.value, upgradeSpots.value] = sortSpots(spotsData.value);
      }

      emit('loaded', data);
    }
  } catch (err) {
    error.value = true;
    errorMessage.value = err.message || 'An error occurred while loading the spots.';
    emit('error', err);
    console.error('SpotListWindlist error:', errorMessage.value.message);
  } finally {
    isLoading.value = false;
  }
};

// Helper function to check if a value is numeric
const isNumeric = (value) => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

// Helper function to parse location name from URL format
const parseLocationName = (urlSegment) => {
  // Replace multiple consecutive dashes with " - "
  let result = urlSegment.replace(/--+/g, ' - ');
  
  // Replace remaining single dashes only if surrounded by non-whitespace characters
  result = result.replace(/(\S)-(\S)/g, '$1 $2');
  
  return result;
};

// Helper function to validate coordinate ranges
const validateCoordinates = (lat, lon) => {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lon);
  
  if (latitude < -90 || latitude > 90) {
    return { valid: false, message: 'Latitude must be between -90 and 90' };
  }
  
  if (longitude < -180 || longitude > 180) {
    return { valid: false, message: 'Longitude must be between -180 and 180' };
  }
  
  return { valid: true };
};

// Helper function to parse search term from path segments
const parseSearchTerm = (searchParts) => {
  // Handle url patterns
  // /search/lat/lon
  // /search/lat/lon/zoom
  // /search/lat/lon/zoom/modelId
  // /search/lat,lon/modelId
  // Any trailing segments that aren't a valid zoom/modelId (e.g. a duplicate
  // "lat,lon" appended by an external link) are discarded.
  const firstHasComma = searchParts[0] && searchParts[0].includes(',');

  if (firstHasComma) {
    searchParts = [...searchParts[0].split(','), ...searchParts.slice(1)];
  }

  if (searchParts.length >= 2 && isNumeric(searchParts[0]) && isNumeric(searchParts[1])) {
    const validation = validateCoordinates(searchParts[0], searchParts[1]);

    if (!validation.valid) {
      return {
        type: 'error',
        searchTerm: null,
        message: validation.message
      };
    }

    const lat = searchParts[0];
    const lon = searchParts[1];

    // Keep only numeric trailing segments — drops secondary "lat,lon" duplicates
    // and any other unrecognized values.
    const extras = searchParts.slice(2).filter(isNumeric);

    // Model IDs are always integers, so reject any non-integer in that slot —
    // this discards a slash-separated "lat/lon" duplicate (e.g. .../47.5/-122.3)
    // that survives the numeric filter above.
    const asModelId = (value) =>
      value !== undefined && Number.isInteger(parseFloat(value)) ? value : -1;

    let zoom = 9;
    let modelId = -1;

    if (firstHasComma) {
      modelId = asModelId(extras[0]);
    } else {
      zoom = extras[0] ?? 9;
      modelId = asModelId(extras[1]);
    }

    return {
      type: 'coords',
      searchTerm: `${lat},${lon}`,
      lat,
      lon,
      zoom,
      modelId,
    };
  }

  if (searchParts.length === 1) {
    return {
      type: 'search',
      searchTerm: parseLocationName(searchParts[0]),
      lat: null,
      lon: null,
      zoom: 9,
      modelId: -1,
    };
  }

  if (searchParts.length === 2 && !isNumeric(searchParts[0]) && !isNumeric(searchParts[1])) {
    // Location name with comma. Ex: plymouth, WA
    return {
      type: 'search',
      searchTerm: parseLocationName(searchParts[0] + searchParts[1]),
      lat: null,
      lon: null,
      zoom: 9,
      modelId: -1,
    };
  }

  return {
    type: 'error',
    searchTerm: null,
    message: 'Invalid URL format. Use a location name or latitude/longitude coordinates.'
  };
};

const handleMapMoved = (data) => {
  // Update map state when user moves the map
  setMapState({
    searchTerm: data.center,
    type: 'coords',
    lat: data.lat,
    lon: data.lon,
    zoom: data.zoom,
  })  

  fetchSpots(data.center);
};

const handleMapLoaded = (event) => {
  const { lat, lon, zoom } = event;
  
  mapLat.value = lat;
  mapLon.value = lon;
  mapZoom.value = zoom;
}

const setMapState = (data) => {
  mapSearchTerm.value = data.searchTerm;
  mapSearchType.value = data.type;
  mapLat.value = data.lat;
  mapLon.value = data.lon;
  mapZoom.value = data.zoom;
}

const handleSortOrderChange = (sortOrder) => {
  googleAnalyticsStore.sendEvent("Windlist_Sort_Order", {
    "Hostname": window.location.hostname,
    "Pathname": window.location.pathname,
    "SortOrder": sortOrder,
  });
}

onMounted(() => {
  displayLiveWindMap.value = displayLiveWind();
  const pathSegments = getPathSegments();

  if (pathSegments.length === 0 ) {
    // If no path segments, use last location from storage or default to "Victoria, AU"
    if (mapState.value && mapState.value.searchTerm) {
      mapSearchTerm.value = mapState.value.searchTerm;
      mapSearchType.value = mapState.value.type;
      mapLat.value = mapState.value.lat;
      mapLon.value = mapState.value.lon;
      mapZoom.value = mapState.value.zoom;
    } else {
      spotsSorted.value = [];
      return;
    }
    fetchSpots(mapSearchTerm.value);
    return;
  }

  const parsed = parseSearchTerm(pathSegments);
  
  if (parsed.type === 'error') {
    error.value = true;
    errorMessage.value = parsed.message;
    return;
  }
    
  // Set map state
  mapSearchTerm.value = parsed.searchTerm;
  mapSearchType.value = parsed.type;
  mapLat.value = parsed.lat;
  mapLon.value = parsed.lon;
  mapZoom.value = parsed.zoom || 9;
  mapModelId.value = parsed.modelId || -1;

  fetchSpots(parsed.searchTerm);
})

watchEffect(() => {
  mapState.value = {
    ...mapState.value,
    searchTerm: mapSearchTerm.value,
    type: mapSearchType.value,
    lat: mapLat.value,
    lon: mapLon.value,
    zoom: mapZoom.value
  }
})

  watch(updateKey.key, () => {
    fetchSpots(mapSearchTerm.value);
  })

</script>

<template>
  <template v-if="displayLiveWindMap">
    <header class="jwx-toolbar-header">
      <div class="jwx-toolbar-header__main-content">
        <div class="jwx-toolbar-header__title">
          <h2 class="jwx-toolbar-header__title-text">{{ mapSearchTerm }}</h2>
          <div class="jwx-toolbar-header__subheader">
            <p class="jwx-toolbar-header__subheader-text">Live Wind</p>
          </div>
        </div>
      </div>
    </header>    
    <DataContainerSection>
      <template #content>
        <DataContainer>
          <LiveWindMap
            :searchTerm="mapSearchTerm"
            :searchType="mapSearchType"
            :lat="mapLat"
            :lon="mapLon"
            :zoom="mapZoom"
            :modelId="mapModelId"
            containerId="windlist-live-wind"
            @mapMoved="handleMapMoved"
            @mapLoaded="handleMapLoaded"
          />            
        </DataContainer>
      </template>
    </DataContainerSection>
  </template>

  <div v-if="showUpgradeBlock" class="jwx-upgrade-block-container" data-content-center="false">
    <div class="jwx-upgrade-block jwx-button-shadow" data-content="stacked">
      <div class="jwx-upgrade-block__content">
        <p class="jwx-upgrade-block__text jwx-text-center">Your missing out on<br><strong>{{totalUpgradeSpots}} Top Ranked Stations!</strong></p>
      </div>
      <div class="jwx-upgrade-block__actions">
        <a href="https://secure.iwindsurf.com/features" target="_blank" class="jwx-button jwx-button--pill jwx-upgrade-block__button">Upgrade Now</a>
        <button class="jwx-button jwx-button--pill  jwx-upgrade-block__button" @click.prevent="showUpgradeSpotModal = true">Preview Stations</button>
      </div>
    </div>
  </div>

  <header class="jwx-toolbar-header">
    <div class="jwx-toolbar-header__main-content">
      <div class="jwx-toolbar-header__title">
        <h2 class="jwx-toolbar-header__title-text">{{ mapSearchTerm }}</h2>
        <div class="jwx-toolbar-header__subheader">
          <p class="jwx-toolbar-header__subheader-text">Wind List</p>
        </div>
      </div>
      <div class="jwx-toolbar-header__actions">
        <SpotSortOrderSelector
          v-model="sortOrder"
          @sortOrder:change="handleSortOrderChange"
          :useDefaultOption="true"
          defaultSelected="default"
          :excludeOptions="['userdefined', 'rank']"
          :surface="0"
        />
      </div>
    </div>
  </header>   

  <DataContainerSection
    :sectionOverflow="false"
  >
    <template v-if="enableDistanceGrouping && hasSpots" #title>{{ closeSpotsSectionTitle }}</template>
    <template #content>
      <DataContainer>
        <div class="jwx-data-container__content">
          <div class="jwx-spot-list-container">
            <div v-if="isLoading" class="jwx-spot-list-loader">
              <DataContainerLoader v-if="isLoading" />
              <p v-else-if="error" class="jwx-spot-list-error-txt">{{ errorMessage }}</p>
            </div>
            <SpotList
              v-if="!isLoading && !error && hasSpots"
              :data="enableDistanceGrouping ? closeSpotsSorted : spotsSorted"
              :showLocalTimestamp="false"
              :showDistance="true"
              :adIndexes="enableDistanceGrouping ? closeAdIndexes : spotsAdIndexes"
            />
            <div class="jwx-p-jumbo" v-else-if="!isLoading && !error && !hasSpots">
              <p class="jwx-text jwx-font-size-lg jwx-text-center jwx-m-jumbo">{{ hasNoSpotsMessage }}</p>
            </div>
          </div>
        </div>
      </DataContainer>
    </template>
  </DataContainerSection>

  <DataContainerSection
    v-if="enableDistanceGrouping && farSpots.length > 0"
    class="jwx-spot-list-container"
    :sectionOverflow="false"
  >
    <template #title>{{ farSpotsSectionTitle }}</template>
    <template #content>
      <DataContainer>
        <div class="jwx-data-container__content">
          <div class="jwx-spot-list-container">
            <SpotList
              v-if="!isLoading && !error"
              :data="farSpotsSorted"
              :showLocalTimestamp="false"
              :showDistance="true"
              :adIndexes="farAdIndexes"
            />
          </div>
        </div>
      </DataContainer>
    </template>
  </DataContainerSection>

  <ModalUpgradeSpots
    v-if="showUpgradeSpotModal"
    :spots="sortedUpgradeSpots"
    :show="showUpgradeSpotModal"
    :groupByDistance="enableDistanceGrouping"
    :distanceUnits="userStore.units.distance"
    @close="showUpgradeSpotModal = false"
  />
  <Suspense>
    <ModalSiteSettings :show="userStore.showSettingsModal" />
  </Suspense>  
  <ModalBrandPromo v-if="showPromoModal" />
</template>
